import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
} from '@avila-roffe/components-web';
import { BookOpen, Check, Search, Wrench } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { CatalogItem } from '../AgentRefsBindingPanel';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  catalog: CatalogItem[];
  catalogLoading?: boolean;
  selectedRefs: string[];
  isSaving: boolean;
  onConfirm: (newRefs: string[]) => Promise<void>;
}

export function ToolSkillPickerDialog({
  open,
  onOpenChange,
  title,
  catalog,
  catalogLoading,
  selectedRefs,
  isSaving,
  onConfirm,
}: Props) {
  const isTool = title.toLowerCase().includes('tool');
  const [query, setQuery] = useState('');
  const [pendingRefs, setPendingRefs] = useState<string[]>(selectedRefs);

  // Reset selection to current server state each time the dialog opens
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only syncs on open
  useEffect(() => {
    if (open) {
      setPendingRefs(selectedRefs);
      setQuery('');
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return catalog;
    return catalog.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.value.toLowerCase().includes(q) ||
        (item.description ?? '').toLowerCase().includes(q) ||
        (item.meta ?? '').toLowerCase().includes(q),
    );
  }, [catalog, query]);

  const displayItems = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const aOn = pendingRefs.includes(a.value) ? 0 : 1;
        const bOn = pendingRefs.includes(b.value) ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
        return a.label.localeCompare(b.label);
      }),
    [filtered, pendingRefs],
  );

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((item) => pendingRefs.includes(item.value));

  function toggle(value: string) {
    setPendingRefs((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  }

  function toggleAll() {
    if (allFilteredSelected) {
      const filteredValues = new Set(filtered.map((i) => i.value));
      setPendingRefs((prev) => prev.filter((r) => !filteredValues.has(r)));
    } else {
      const filteredValues = filtered.map((i) => i.value);
      setPendingRefs((prev) => [...new Set([...prev, ...filteredValues])]);
    }
  }

  async function handleConfirm() {
    await onConfirm(pendingRefs);
    onOpenChange(false);
  }

  const TypeIcon = isTool ? Wrench : BookOpen;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 p-0 sm:max-w-lg max-h-[80vh]">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Select {title}</DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${title.toLowerCase()}…`}
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {pendingRefs.length} selected · {catalog.length} available
            </p>
            {filtered.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-primary hover:underline"
              >
                {allFilteredSelected ? 'Unselect all' : 'Select all'}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 min-h-0">
          {catalogLoading && (
            <div className="flex justify-center py-8">
              <Spinner className="size-5" />
            </div>
          )}

          {!catalogLoading && displayItems.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">
              No {title.toLowerCase()} match your search.
            </p>
          )}

          {!catalogLoading &&
            displayItems.map((item) => {
              const isSelected = pendingRefs.includes(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => toggle(item.value)}
                  data-selected={isSelected}
                  className="w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors cursor-pointer hover:bg-muted/50 data-[selected=true]:border-primary/40 data-[selected=true]:bg-primary/5"
                >
                  <div
                    className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
                      isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <TypeIcon className="size-3.5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{item.label}</p>
                    <p className="text-[11px] font-mono text-muted-foreground truncate">
                      {item.value}
                    </p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {item.status && item.status !== 'ACTIVE' && (
                    <Badge
                      variant={item.status === 'DRAFT' ? 'warning' : 'destructive'}
                      appearance="soft"
                      className="text-[10px] shrink-0"
                    >
                      {item.status.charAt(0) + item.status.slice(1).toLowerCase()}
                    </Badge>
                  )}

                  {isSelected && <Check className="size-4 shrink-0 text-primary" />}
                </button>
              );
            })}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button
            type="button"
            appearance="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleConfirm()} disabled={isSaving}>
            {isSaving && <Spinner className="size-3.5 mr-2" />}
            Confirm ({pendingRefs.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
